import { useEffect, useState } from "react";
import { BaoRig, solveFront } from "../../game/bao/BaoRig";
import { NEUTRAL, duration, keyPose, lerpPose, sample, swapLR } from "../../game/bao/rig";
import { useBaoModel } from "../three/useBaoModel";
import { Bao3DActor } from "../three/Bao3DActor";

/** Bao seen from behind (third-person), driven by the patient's simulated input: reaches move arms + weight shift, leg lifts move legs. */
export const rigBox = ({ w, h }) => { const W = Math.min(w * 0.5, 210), H = (W * 460) / 300; return { W, H, left: w / 2 - W / 2, top: h - H * 0.99 }; };
const toSvg = (b, [X, Y]) => [((X - b.left) / b.W) * 300, ((Y - b.top) / b.H) * 460 - 40];
const toScreen = (b, [x, y]) => [b.left + (x / 300) * b.W, b.top + ((y + 40) / 460) * b.H];
const KEY = { left: "L", right: "R" }, SGN = { left: -1, right: 1 };
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

export const drivenPose = (base, size, hands, limb = "arm") => {
  const act = ["left", "right"].filter((s) => hands[s]);
  if (!act.length || !size.w) return base;
  if (limb === "leg") {
    const s = act[0], amt = clamp((0.92 - hands[s].y) / 0.6, 0, 1), lift = lerpPose(base, keyPose("balance", 1), amt);
    return s === "right" ? lift : { ...swapLR(lift), px: -lift.px, torso: -lift.torso };
  }
  const b = rigBox(size), g0 = solveFront(NEUTRAL, true), avgX = act.reduce((n, s) => n + hands[s].x, 0) / act.length - 0.5;
  let p = act.length === 1 ? lerpPose(base, keyPose("weightShift", avgX < 0 ? 1 : 3), Math.min(1, Math.abs(avgX) * 2.4)) : { ...base };
  p = { ...p, torso: act.length === 1 ? clamp(avgX * 24, -9, 9) : 0 };
  act.forEach((s) => {
    const sh = g0.arms[KEY[s]].sh, [tx, ty] = toSvg(b, [hands[s].x * size.w, hands[s].y * size.h]);
    p[`a${KEY[s]}`] = { ...p[`a${KEY[s]}`], a: clamp((Math.atan2(SGN[s] * (tx - sh[0]), ty - sh[1]) * 180) / Math.PI, -25, 178), e: 6 };
  });
  return p;
};

/** Screen position of Bao's wrist for a reach toward `aim` (used to launch projectiles from his hand). */
export const rigHand = (size, side, aim) => {
  const p = drivenPose(NEUTRAL, size, { [side]: { x: aim[0] / size.w, y: aim[1] / size.h } });
  return toScreen(rigBox(size), solveFront(p, true).arms[KEY[side]].wr);
};

const useClock = () => {
  const [t, setT] = useState(0);
  useEffect(() => { let raf; const t0 = performance.now(); const loop = (n) => { setT((n - t0) / 1000); raf = requestAnimationFrame(loop); }; raf = requestAnimationFrame(loop); return () => cancelAnimationFrame(raf); }, []);
  return t;
};

export const BaoInGame = ({ size, hands = {}, limb = "arm", cheer = 0, weapon = null, WeaponComp = null }) => {
  const t = useClock(), model = useBaoModel();
  const [cheerAt, setCheerAt] = useState(null);
  useEffect(() => { if (cheer) setCheerAt(performance.now()); }, [cheer]);
  if (!size.w) return null;
  const ct = cheerAt != null ? (performance.now() - cheerAt) / 1000 : null;
  const cheering = ct != null && ct < duration("jump");
  const base = cheering ? sample("jump", ct) : sample("idle", t % duration("idle"));
  const pose = cheering ? base : drivenPose(base, size, hands, limb);
  const b = rigBox(size), active = ["left", "right"].filter((s) => hands[s]);
  return (
    <div className="pointer-events-none absolute inset-0" data-testid="bao-avatar">
      {model ? (
        <div className="absolute" style={{ left: b.left - b.W * 0.15, top: b.top, width: b.W * 1.3, height: b.H }} data-limb={limb}>
          <Bao3DActor model={model} pose={pose} view="back" clip={cheering ? "celebrate" : null} />
        </div>
      ) : (
        <div className="absolute drop-shadow-xl" style={{ left: b.left, top: b.top, width: b.W }} data-testid="bao-rig-ingame" data-limb={limb}>
          <BaoRig pose={pose} view="back" width="100%" />
        </div>
      )}
      {weapon && WeaponComp && active.map((s) => {
        const aim = [hands[s].x * size.w, hands[s].y * size.h], at = rigHand(size, s, aim);
        return <WeaponComp key={s} kind={weapon} at={at} angle={(Math.atan2(aim[1] - at[1], aim[0] - at[0]) * 180) / Math.PI} />;
      })}
    </div>
  );
};
